import { registerLocaleData } from '@angular/common';
import cs from '@angular/common/locales/cs';
import {
  inject,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { TitleStrategy } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { IntercomConfig } from 'intercom';
import {
  AnalyticsModule,
  GTM_CONTAINER_ID,
  GTM_MARKETING_CONTAINER_ID,
  HotkeysService,
  LocalNumberPipe,
  TitleService,
} from 'wflow-cdk';
import { WfDownloadModule } from 'wflow-download';
import { provideStatsig } from 'wflow-features/feature-flags';
import { providePaginatorTracking } from 'wflow-features/product-analytics';
import { WfNavigationModule } from 'wflow-features/navigation/navigation.module';
import { WfNavigationStateStore } from 'wflow-features/navigation/services/state-store.service';
import {
  WfNotificationService,
  WfNotificationsPileComponent,
  WfToastTimerService,
} from 'wflow-features/notifications';
import { WfFlagIconModule } from 'wflow-flag-icons';
import { ICONS } from 'wflow-main-app/src/app/configs/icons.const';
import { ImportFileDialogComponentModule } from 'wflow-main-app/src/app/shared/components/import-file-dialog/import-file-dialog.module';
import { WfSecurityModule } from 'wflow-security';
import {
  IconsModule,
  REGISTER_FACTORY,
  REGISTER_RENDER_FACTORY,
  WfAvatarService,
  WUI_DIALOG_CONFIG,
  WUI_DIALOG_CONFIG_DEFAULT,
} from 'wflow-ui';
import { WUI_PRINT, WuiPrint } from 'wflow-ui/file-preview';
import { wfPrint } from 'wflow-print';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthConfigModule } from './auth/auth-config.module';
import { ColorModeRequests } from './configs/color-mode.requests';
import { HOTKEYS_CONFIG } from './configs/hotkeys.const';
import { SOLID_ICONS } from './configs/solid-icons.const';
import {
  InitTranslateFactory,
  TRANSLATION_CONFIG,
} from './configs/translate.const';
import { providePeppolApi } from './core/api/api/peppol/peppol-api.providers';
import { TopNavigationComponent } from './core/components/top-navigation/top-navigation.component';
import { CoreModule } from './core/core.module';
import { ContextSelectorResolver } from './core/resolvers/context-selector.resolver';
import { AzureAnalyticsService } from './core/services/analytics/azure-analytics.service';
import { AvatarService } from './core/services/avatar.service';
import { WfOnAppInitService } from './core/services/on-app-init.service';
import { RoleProviderService } from './core/services/role-provider.service';
import { CacheModule } from './core/services/service-workers/cache.module';
import { VersionService } from './core/services/version.service';
import { COLOR_MODE_REQUESTS, ColorModeService } from './features/color-mode';
import { ColorModeModule } from './features/color-mode/color-mode.module';
import { IntercomButtonComponent } from './shared/components/intercom-button/intercom-button';
import {
  CONTEXT_SELECTOR_TOKEN,
  PageContainerComponent,
} from './shared/components/page-container';
import { MatModule } from './shared/modules/material/mat.module';
import {
  GLOBAL_NAVIGATION,
  GLOBAL_NAVIGATION_PARAMS,
  NavStateCacheService,
} from './shared/services/navigation';
import { RegisterFactoryService } from './shared/services/registers/register-factory.service';
import { VatCountryTypeService } from './shared/services/vat-country-type-translations/vat-country-type.service';
import { REGISTER_RENDER_MAP } from './shared/utils/register-render.const';
import { GlobalStoreModule } from './store/store.module';

registerLocaleData(cs);

@NgModule({
  declarations: [AppComponent],
  imports: [
    // angular
    BrowserModule,

    // analytics
    AnalyticsModule.forRoot({ provider: AzureAnalyticsService }),

    // core
    AuthConfigModule,
    CoreModule,

    // app
    AppRoutingModule,

    // translate
    TranslateModule.forRoot(TRANSLATION_CONFIG),

    // navigation
    WfNavigationModule.forRoot(GLOBAL_NAVIGATION, GLOBAL_NAVIGATION_PARAMS),

    // global store
    GlobalStoreModule,

    // 3rd party
    WfSecurityModule.forRoot({
      useClass: RoleProviderService,
    }),
    IconsModule.forRoot({
      outline: ICONS,
      solid: SOLID_ICONS,
    }),

    CacheModule.register('service-worker.js', {
      enabled: true,
      disableStaticCache: environment.sw?.disableStaticCache,
      disableDynamicCache: environment.sw?.disableDynamicCache,
      static: [/\.js$/, /\.css$/, /\/assets\//, /\/translations\//],
    }),

    // layout
    IntercomButtonComponent,
    TopNavigationComponent,
    PageContainerComponent,

    ImportFileDialogComponentModule,
    ColorModeModule,
    WfDownloadModule.forRoot(
      {
        name: 'WF_DOWNLOAD_STATE',
        storage: sessionStorage,
      },
      'wflow-file',
    ),

    WfFlagIconModule.forRoot('/assets/flag-icons'),

    MatModule,
    WfNotificationsPileComponent,
  ],
  providers: [
    provideZoneChangeDetection(),

    provideAppInitializer(() => {
      InitTranslateFactory();
      inject(WfOnAppInitService).init();
      inject(ColorModeService).init();
      inject(VersionService).runChecker();
      inject(HotkeysService).registerHotkeys(HOTKEYS_CONFIG);
    }),

    provideStatsig(),

    providePaginatorTracking(),

    {
      provide: COLOR_MODE_REQUESTS,
      useClass: ColorModeRequests,
    },

    providePeppolApi(),

    // wflow-ui may not import a layer-4 library, so the app supplies the print function.
    // `satisfies`: ValueProvider.useValue is typed `any`, so this is the one place where both
    // WuiPrint and wfPrint are visible and the two can still be checked against each other.
    // Without it, renaming a member of WfPrintTypes would only surface as a runtime
    // `Invalid print type`, where the pre-token direct import failed at compile time.
    { provide: WUI_PRINT, useValue: wfPrint satisfies WuiPrint },

    { provide: GTM_CONTAINER_ID, useValue: 'GTM-58DN69PX' },
    { provide: GTM_MARKETING_CONTAINER_ID, useValue: 'GTM-TTTCLWC' },

    LocalNumberPipe,

    VatCountryTypeService,

    {
      provide: REGISTER_FACTORY,
      useClass: RegisterFactoryService,
    },
    {
      provide: REGISTER_RENDER_FACTORY,
      useValue: REGISTER_RENDER_MAP,
    },

    {
      provide: WUI_DIALOG_CONFIG,
      useValue: {
        ...WUI_DIALOG_CONFIG_DEFAULT,
        draggable: true,
        closeOnOverlayClick: false,
      },
    },

    {
      provide: WfNavigationStateStore,
      useExisting: NavStateCacheService,
    },

    {
      provide: CONTEXT_SELECTOR_TOKEN,
      useClass: ContextSelectorResolver,
    },

    {
      provide: TitleStrategy,
      useExisting: TitleService,
    },

    {
      provide: IntercomConfig,
      useValue: {
        appId: environment.intercomKey, // from your Intercom config
        updateOnRouterChange: true, // will automatically run `update` on router event changes. Default: `false`
      },
    },
    {
      provide: WfAvatarService,
      useClass: AvatarService,
    },
    WfNotificationService,
    WfToastTimerService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
